import React, { useEffect, useState, useRef } from 'react';
import { Card } from '../../ui/Card';
import { Button } from '../../ui/Button';
import { Input } from '../../ui/Input';
import { Badge } from '../../ui/Badge';
import { SearchIcon, PlusIcon, FilterIcon, MailIcon, BuildingIcon, UserIcon, MoreVerticalIcon, CheckCircleIcon, XCircleIcon, ClockIcon, XIcon, BanIcon, EditIcon, LinkIcon, UserXIcon, LogOutIcon, TrendingUpIcon, UserPlusIcon, ShieldIcon, UsersIcon, ShoppingCartIcon, MicIcon } from 'lucide-react';
import { AdminUser, PermissionProfile, UserStatus } from '../../../types/users';
import { ExhibitorSelector } from '../shared/ExhibitorSelector';
import { LinkingStatusBadge } from '../shared/LinkingStatusBadge';
import { useEvent } from '../../../contexts/EventContext';
import { attendees, AttendeeData } from '../../../data/attendees';
interface UsersManagementProps {
  onBack: () => void;
  onNavigateToUser?: (user: AdminUser) => void;
}
// Convert AttendeeData to AdminUser format
const convertAttendeeToUser = (attendee: AttendeeData): AdminUser => {
  // Randomly assign 10% as self-registered
  const isSelfRegistered = Math.random() < 0.1;
  // Determine permissions (users can have multiple)
  const permissions: PermissionProfile[] = [];
  if (isSelfRegistered) {
    permissions.push('general-public');
  } else {
    // All non-self-registered users get B2B access
    permissions.push('b2b-access');
    // Add Buyer permission for buyer roles
    if (attendee.role.toLowerCase().includes('buyer') || attendee.role.toLowerCase().includes('procurement')) {
      permissions.push('buyer');
    }
    // Randomly assign 15% of B2B users as speakers
    if (Math.random() < 0.15) {
      permissions.push('speaker');
    }
  }
  // Special case: Sarah Smith is B2B Access + Buyer + Speaker
  if (attendee.id === 'attendee-0') {
    permissions.length = 0; // Clear existing
    permissions.push('b2b-access', 'buyer', 'speaker');
  }
  // Generate random dates for variety
  const daysAgo = Math.floor(Math.random() * 30);
  const createdDate = new Date();
  createdDate.setDate(createdDate.getDate() - daysAgo - 10);
  const lastLoginDaysAgo = Math.floor(Math.random() * daysAgo);
  const lastLoginDate = new Date();
  lastLoginDate.setDate(lastLoginDate.getDate() - lastLoginDaysAgo);
  // Randomly assign status (mostly active)
  const statusRandom = Math.random();
  const status: UserStatus = statusRandom > 0.95 ? 'suspended' : statusRandom > 0.85 ? 'pending' : 'active';
  // Randomly determine if they've logged in (80% have)
  const hasLoggedIn = Math.random() > 0.2;
  return {
    id: attendee.id,
    email: attendee.email,
    name: attendee.name,
    permissions,
    status,
    registrationSource: isSelfRegistered ? 'self-registered' : 'admin-invited',
    companyId: attendee.source === 'exhibitor' && !isSelfRegistered ? attendee.id : undefined,
    companyName: attendee.source === 'exhibitor' && !isSelfRegistered ? attendee.company : undefined,
    role: attendee.role,
    isExhibiting: attendee.source === 'exhibitor' && !isSelfRegistered,
    standNumber: attendee.source === 'exhibitor' && !isSelfRegistered ? `${String.fromCharCode(65 + Math.floor(Math.random() * 10))}${Math.floor(Math.random() * 99) + 1}` : undefined,
    createdAt: createdDate.toISOString().split('T')[0],
    lastLogin: hasLoggedIn ? lastLoginDate.toISOString().split('T')[0] : undefined,
    eventAccess: attendee.interests.includes('Fitness') ? ['active', 'muscle'] : ['industry'],
    isPrimaryContact: attendee.source === 'exhibitor' && !isSelfRegistered && Math.random() > 0.7,
    interests: attendee.interests
  };
};
export function UsersManagement({
  onBack,
  onNavigateToUser
}: UsersManagementProps) {
  const {
    currentEvent
  } = useEvent();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPermissions, setSelectedPermissions] = useState<PermissionProfile[]>([]);
  const [selectedRegistrationSource, setSelectedRegistrationSource] = useState<'self-registered' | 'admin-invited' | 'all'>('all');
  const [selectedStatus, setSelectedStatus] = useState<UserStatus | 'all'>('all');
  // Secondary filter for Speakers/Buyers
  const [secondaryFilter, setSecondaryFilter] = useState<'all' | 'speakers' | 'buyers'>('all');
  // Quick filter states
  const [filterNoExhibitor, setFilterNoExhibitor] = useState(false);
  const [filterNotLoggedIn, setFilterNotLoggedIn] = useState(false);
  const [filterIncompleteProfile, setFilterIncompleteProfile] = useState(false);
  const [filterIncompleteOnboarding, setFilterIncompleteOnboarding] = useState(false);
  const [showFiltersDropdown, setShowFiltersDropdown] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [userToLink, setUserToLink] = useState<AdminUser | null>(null);
  const [activeActionMenu, setActiveActionMenu] = useState<string | null>(null);
  const filtersRef = useRef<HTMLDivElement>(null);
  const actionsMenuRef = useRef<HTMLDivElement>(null);
  // Invite form state
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [invitePermissions, setInvitePermissions] = useState<PermissionProfile[]>(['b2b-access']);
  const [inviteRole, setInviteRole] = useState('');
  // Multi-exhibitor selection state
  const [selectedExhibitorIds, setSelectedExhibitorIds] = useState<string[]>([]);
  const [showCreateExhibitor, setShowCreateExhibitor] = useState(false);
  const [skipExhibitorLink, setSkipExhibitorLink] = useState(false);
  // Quick exhibitor creation fields
  const [newExhibitorName, setNewExhibitorName] = useState('');
  const [newExhibitorStand, setNewExhibitorStand] = useState('');
  const [newExhibitorEvents, setNewExhibitorEvents] = useState<string[]>(['DAI']);
  // Convert attendees data to users format
  const [users, setUsers] = useState<AdminUser[]>(() => attendees.map(convertAttendeeToUser));
  // Close filters dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (filtersRef.current && !filtersRef.current.contains(event.target as Node)) {
        setShowFiltersDropdown(false);
      }
    };
    if (showFiltersDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showFiltersDropdown]);
  // Close actions menu on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (actionsMenuRef.current && !actionsMenuRef.current.contains(event.target as Node)) {
        setActiveActionMenu(null);
      }
    };
    if (activeActionMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [activeActionMenu]);
  // Mock exhibitors data
  const mockExhibitors = [{
    id: '1',
    name: 'Fitness First',
    standNumber: 'A12',
    events: ['DAI', 'DAS']
  }, {
    id: '2',
    name: 'GymNation',
    standNumber: 'B5',
    events: ['DAS']
  }, {
    id: '3',
    name: 'ORBIT4',
    standNumber: 'GP4',
    events: ['DAI']
  }, {
    id: '4',
    name: 'Rolla',
    standNumber: 'F5B',
    events: ['DAS']
  }];
  const handleSuspendUser = (userId: string) => {
    setUsers(prevUsers => prevUsers.map(user => user.id === userId ? {
      ...user,
      status: 'suspended' as UserStatus
    } : user));
    setActiveActionMenu(null);
  };
  const handleUnsuspendUser = (userId: string) => {
    setUsers(prevUsers => prevUsers.map(user => user.id === userId ? {
      ...user,
      status: 'active' as UserStatus
    } : user));
    setActiveActionMenu(null);
  };
  const clearFilters = () => {
    setSelectedPermissions([]);
    setSelectedRegistrationSource('all');
    setSelectedStatus('all');
    setFilterNoExhibitor(false);
    setFilterNotLoggedIn(false);
    setFilterIncompleteProfile(false);
    setFilterIncompleteOnboarding(false);
  };
  const togglePermission = (permission: PermissionProfile) => {
    setSelectedPermissions(prev => prev.includes(permission) ? prev.filter(p => p !== permission) : [...prev, permission]);
  };
  const activeFiltersCount = selectedPermissions.length + (selectedRegistrationSource !== 'all' ? 1 : 0) + (selectedStatus !== 'all' ? 1 : 0) + (filterNoExhibitor ? 1 : 0) + (filterNotLoggedIn ? 1 : 0) + (filterIncompleteProfile ? 1 : 0) + (filterIncompleteOnboarding ? 1 : 0);
  const filteredUsers = users.filter(user => {
    const matchesSearch = searchQuery === '' || user.name.toLowerCase().includes(searchQuery.toLowerCase()) || user.email.toLowerCase().includes(searchQuery.toLowerCase()) || user.companyName?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesPermissions = selectedPermissions.length === 0 || selectedPermissions.every(permission => user.permissions.includes(permission));
    const matchesRegistrationSource = selectedRegistrationSource === 'all' || user.registrationSource === selectedRegistrationSource;
    const matchesStatus = selectedStatus === 'all' || user.status === selectedStatus;
    // Secondary filter for Speakers/Buyers
    const matchesSecondaryFilter = secondaryFilter === 'all' || secondaryFilter === 'speakers' && user.permissions.includes('speaker') || secondaryFilter === 'buyers' && user.permissions.includes('buyer');
    // Quick filter: No Exhibitor
    const matchesNoExhibitor = !filterNoExhibitor || user.permissions.includes('b2b-access') && !user.companyName;
    // Quick filter: Not Logged In
    const matchesNotLoggedIn = !filterNotLoggedIn || !user.lastLogin;
    // Quick filter: Incomplete Profile (placeholder logic)
    const matchesIncompleteProfile = !filterIncompleteProfile || true; // TODO: Add real logic
    // Quick filter: Incomplete Onboarding (placeholder logic)
    const matchesIncompleteOnboarding = !filterIncompleteOnboarding || true; // TODO: Add real logic
    return matchesSearch && matchesPermissions && matchesRegistrationSource && matchesStatus && matchesSecondaryFilter && matchesNoExhibitor && matchesNotLoggedIn && matchesIncompleteProfile && matchesIncompleteOnboarding;
  });
  const getPermissionLabel = (permission: PermissionProfile) => {
    switch (permission) {
      case 'general-public':
        return 'General Public';
      case 'b2b-access':
        return 'B2B Access';
      case 'buyer':
        return 'Buyer';
      case 'speaker':
        return 'Speaker';
    }
  };
  const getPermissionBadgeVariant = (permission: PermissionProfile) => {
    switch (permission) {
      case 'general-public':
        return 'default';
      case 'b2b-access':
        return 'success';
      case 'buyer':
        return 'warning';
      case 'speaker':
        return 'error';
    }
  };
  const getPermissionGroupLabel = (group: PermissionGroup) => {
    switch (group) {
      case 'exhibitor':
        return 'Exhibitor';
      case 'buyer':
        return 'Buyer';
      case 'speaker':
        return 'Speaker';
    }
  };
  const getPermissionGroupIcon = (group: PermissionGroup) => {
    switch (group) {
      case 'exhibitor':
        return <StoreIcon className="w-3 h-3" data-id="element-2783" />;
      case 'buyer':
        return <ShoppingCartIcon className="w-3 h-3" data-id="element-2784" />;
      case 'speaker':
        return <MicIcon className="w-3 h-3" data-id="element-2785" />;
    }
  };
  const getRegistrationSourceLabel = (source: 'self-registered' | 'admin-invited') => {
    return source === 'self-registered' ? 'Self Registered' : 'Invited';
  };
  const getStatusBadgeVariant = (status: UserStatus) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'pending':
        return 'warning';
      case 'suspended':
        return 'error';
    }
  };
  const getStatusIcon = (status: UserStatus) => {
    switch (status) {
      case 'active':
        return <CheckCircleIcon className="w-4 h-4" data-id="element-2786" />;
      case 'pending':
        return <ClockIcon className="w-4 h-4" data-id="element-2787" />;
      case 'suspended':
        return <XCircleIcon className="w-4 h-4" data-id="element-2788" />;
    }
  };
  const handleOpenInviteModal = () => {
    setInviteEmail('');
    setInviteName('');
    setInvitePermissions(['b2b-access']);
    setInviteRole('');
    setSelectedExhibitorIds([]);
    setShowCreateExhibitor(false);
    setSkipExhibitorLink(false);
    setNewExhibitorName('');
    setNewExhibitorStand('');
    setNewExhibitorEvents(['DAI']);
    setShowInviteModal(true);
  };
  const toggleInvitePermission = (permission: PermissionProfile) => {
    setInvitePermissions(prev => {
      const newPermissions = prev.includes(permission) ? prev.filter(p => p !== permission) : [...prev, permission];
      // Ensure B2B Access is included when Buyer or Speaker is selected
      if ((permission === 'buyer' || permission === 'speaker') && !prev.includes(permission)) {
        if (!newPermissions.includes('b2b-access')) {
          newPermissions.push('b2b-access');
        }
      }
      // Remove General Public if adding B2B-related permissions
      if (permission !== 'general-public' && newPermissions.includes('general-public')) {
        return newPermissions.filter(p => p !== 'general-public');
      }
      // Remove B2B-related permissions if adding General Public
      if (permission === 'general-public') {
        return ['general-public'];
      }
      return newPermissions;
    });
  };
  const handleInviteUser = () => {
    console.log('Inviting user:', {
      email: inviteEmail,
      name: inviteName,
      permissionProfile: invitePermissions,
      role: inviteRole,
      exhibitorIds: selectedExhibitorIds,
      newExhibitor: showCreateExhibitor ? {
        name: newExhibitorName,
        standNumber: newExhibitorStand,
        events: newExhibitorEvents
      } : null
    });
    setShowInviteModal(false);
  };
  const handleOpenLinkModal = (user: AdminUser) => {
    setUserToLink(user);
    setSelectedExhibitorIds([]);
    setShowLinkModal(true);
  };
  const handleLinkUser = () => {
    if (!userToLink || selectedExhibitorIds.length === 0) return;
    console.log('Linking user:', userToLink.id, 'to exhibitors:', selectedExhibitorIds);
    setShowLinkModal(false);
    setUserToLink(null);
  };
  const handleAddExhibitor = (exhibitorId: string | null) => {
    if (exhibitorId && !selectedExhibitorIds.includes(exhibitorId)) {
      setSelectedExhibitorIds([...selectedExhibitorIds, exhibitorId]);
    }
  };
  const handleRemoveExhibitor = (exhibitorId: string) => {
    setSelectedExhibitorIds(selectedExhibitorIds.filter(id => id !== exhibitorId));
  };
  const availableEvents = [{
    id: 'DAI',
    name: 'Dubai Active Industry'
  }, {
    id: 'DAS',
    name: 'Dubai Active Show'
  }, {
    id: 'DMS',
    name: 'Dubai Muscle Show'
  }];
  const handleUserClick = (user: AdminUser) => {
    if (onNavigateToUser) {
      onNavigateToUser(user);
    }
  };
  return <div className="pb-24" data-id="element-2789">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 px-4 sm:px-0" data-id="element-2790">
        <div data-id="element-2791">
          <h2 className="text-2xl font-bold text-gray-900" data-id="element-2792">Users Management</h2>
          <p className="text-gray-500 mt-1" data-id="element-2793">
            Manage app users, invitations, and exhibitor links
          </p>
        </div>
        <Button variant="primary" onClick={handleOpenInviteModal} data-id="element-2794">
          <PlusIcon className="w-4 h-4 mr-2" data-id="element-2795" />
          Invite User
        </Button>
      </div>

      {/* Secondary Navigation - Speakers/Buyers Filter */}
      <div className="mb-6 px-4 sm:px-0" data-id="element-2796">
        <div className="flex gap-2 border-b border-gray-200" data-id="element-2797">
          <button onClick={() => setSecondaryFilter('all')} className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${secondaryFilter === 'all' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-2798">
            All Users
          </button>
          <button onClick={() => setSecondaryFilter('speakers')} className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors flex items-center gap-2 ${secondaryFilter === 'speakers' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-2799">
            <MicIcon className="w-4 h-4" data-id="element-2800" />
            Speakers
            <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full" data-id="element-2801">
              {users.filter(u => u.permissions.includes('speaker')).length}
            </span>
          </button>
          <button onClick={() => setSecondaryFilter('buyers')} className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors flex items-center gap-2 ${secondaryFilter === 'buyers' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-2802">
            <ShoppingCartIcon className="w-4 h-4" data-id="element-2803" />
            Buyers
            <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full" data-id="element-2804">
              {users.filter(u => u.permissions.includes('buyer')).length}
            </span>
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6 px-4 sm:px-0" data-id="element-2805">
        <Card padding="md" data-id="element-2806">
          <div className="flex items-center justify-between" data-id="element-2807">
            <div data-id="element-2808">
              <p className="text-xs sm:text-sm text-gray-500" data-id="element-2809">Total</p>
              <p className="text-xl sm:text-2xl font-bold text-gray-900" data-id="element-2810">
                {users.length}
              </p>
            </div>
            <UserIcon className="w-6 h-6 sm:w-8 sm:h-8 text-blue-500" data-id="element-2811" />
          </div>
        </Card>
        <Card padding="md" data-id="element-2812">
          <div className="flex items-center justify-between" data-id="element-2813">
            <div data-id="element-2814">
              <p className="text-xs sm:text-sm text-gray-500" data-id="element-2815">B2B Access</p>
              <p className="text-xl sm:text-2xl font-bold text-gray-900" data-id="element-2816">
                {users.filter(u => u.permissions.includes('b2b-access')).length}
              </p>
            </div>
            <ShieldIcon className="w-6 h-6 sm:w-8 sm:h-8 text-green-500" data-id="element-2817" />
          </div>
        </Card>
        <Card padding="md" data-id="element-2818">
          <div className="flex items-center justify-between" data-id="element-2819">
            <div data-id="element-2820">
              <p className="text-xs sm:text-sm text-gray-500" data-id="element-2821">Buyers</p>
              <p className="text-xl sm:text-2xl font-bold text-gray-900" data-id="element-2822">
                {users.filter(u => u.permissions.includes('buyer')).length}
              </p>
            </div>
            <ShoppingCartIcon className="w-6 h-6 sm:w-8 sm:h-8 text-purple-500" data-id="element-2823" />
          </div>
        </Card>
        <Card padding="md" data-id="element-2824">
          <div className="flex items-center justify-between" data-id="element-2825">
            <div data-id="element-2826">
              <p className="text-xs sm:text-sm text-gray-500" data-id="element-2827">Speakers</p>
              <p className="text-xl sm:text-2xl font-bold text-gray-900" data-id="element-2828">
                {users.filter(u => u.permissions.includes('speaker')).length}
              </p>
            </div>
            <MicIcon className="w-6 h-6 sm:w-8 sm:h-8 text-orange-500" data-id="element-2829" />
          </div>
        </Card>
      </div>

      {/* Enhanced Filters Bar */}
      <div className="bg-white p-4 rounded-lg border border-gray-200 mb-6" data-id="element-2830">
        <div className="flex gap-4 items-start" data-id="element-2831">
          {/* Search */}
          <div className="relative flex-1 max-w-md" data-id="element-2832">
            <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" data-id="element-2833" />
            <input type="text" placeholder="Search by name, email, or company..." className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-inter text-sm" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-2834" />
          </div>

          {/* Quick Filters */}
          <div className="flex gap-2 flex-wrap" data-id="element-2835">
            <button onClick={() => setFilterNoExhibitor(!filterNoExhibitor)} className={`px-3 py-2 text-sm font-medium rounded-lg border transition-colors ${filterNoExhibitor ? 'bg-blue-50 border-blue-200 text-blue-700' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'}`} data-id="element-2836">
              <UserXIcon className="w-4 h-4 inline mr-1.5" data-id="element-2837" />
              No Exhibitor
            </button>

            <button onClick={() => setFilterNotLoggedIn(!filterNotLoggedIn)} className={`px-3 py-2 text-sm font-medium rounded-lg border transition-colors ${filterNotLoggedIn ? 'bg-blue-50 border-blue-200 text-blue-700' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'}`} data-id="element-2838">
              <LogOutIcon className="w-4 h-4 inline mr-1.5" data-id="element-2839" />
              Not Logged In
            </button>

            <button onClick={() => setFilterIncompleteProfile(!filterIncompleteProfile)} className={`px-3 py-2 text-sm font-medium rounded-lg border transition-colors ${filterIncompleteProfile ? 'bg-orange-50 border-orange-200 text-orange-700' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'}`} data-id="element-2840">
              <TrendingUpIcon className="w-4 h-4 inline mr-1.5" data-id="element-2841" />
              Incomplete Profiles
            </button>

            <button onClick={() => setFilterIncompleteOnboarding(!filterIncompleteOnboarding)} className={`px-3 py-2 text-sm font-medium rounded-lg border transition-colors ${filterIncompleteOnboarding ? 'bg-purple-50 border-purple-200 text-purple-700' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'}`} data-id="element-2842">
              <UserPlusIcon className="w-4 h-4 inline mr-1.5" data-id="element-2843" />
              Incomplete Onboarding
            </button>
          </div>

          {/* More Filters Dropdown */}
          <div className="relative" ref={filtersRef} data-id="element-2844">
            <Button variant="outline" size="sm" onClick={() => setShowFiltersDropdown(!showFiltersDropdown)} data-id="element-2845">
              <FilterIcon className="w-4 h-4 mr-2" data-id="element-2846" />
              More Filters
              {activeFiltersCount > 0 && <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full" data-id="element-2847">
                  {activeFiltersCount}
                </span>}
            </Button>

            {showFiltersDropdown && <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-xl border border-gray-200 z-50 max-h-[500px] overflow-y-auto" data-id="element-2848">
                <div className="p-4" data-id="element-2849">
                  <div className="flex items-center justify-between mb-4 sticky top-0 bg-white pb-2 border-b border-gray-100" data-id="element-2850">
                    <h3 className="text-sm font-semibold text-gray-900" data-id="element-2851">
                      Advanced Filters
                    </h3>
                    {activeFiltersCount > 0 && <button onClick={clearFilters} className="text-xs text-blue-600 hover:text-blue-700" data-id="element-2852">
                        Clear all
                      </button>}
                  </div>

                  {/* Permission Filters */}
                  <div className="mb-4" data-id="element-2853">
                    <label className="text-xs font-medium text-gray-700 mb-2 block" data-id="element-2854">
                      Permissions
                    </label>
                    <div className="space-y-2" data-id="element-2855">
                      <label className="flex items-center gap-2 cursor-pointer" data-id="element-2856">
                        <input type="checkbox" checked={selectedPermissions.includes('general-public')} onChange={() => togglePermission('general-public')} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" data-id="element-2857" />
                        <span className="text-sm text-gray-700" data-id="element-2858">
                          General Public
                        </span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer" data-id="element-2859">
                        <input type="checkbox" checked={selectedPermissions.includes('b2b-access')} onChange={() => togglePermission('b2b-access')} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" data-id="element-2860" />
                        <span className="text-sm text-gray-700" data-id="element-2861">
                          B2B Access
                        </span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer" data-id="element-2862">
                        <input type="checkbox" checked={selectedPermissions.includes('buyer')} onChange={() => togglePermission('buyer')} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" data-id="element-2863" />
                        <span className="text-sm text-gray-700" data-id="element-2864">Buyer</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer" data-id="element-2865">
                        <input type="checkbox" checked={selectedPermissions.includes('speaker')} onChange={() => togglePermission('speaker')} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" data-id="element-2866" />
                        <span className="text-sm text-gray-700" data-id="element-2867">Speaker</span>
                      </label>
                    </div>
                  </div>

                  {/* Registration Source Filter */}
                  <div className="mb-4" data-id="element-2868">
                    <label className="text-xs font-medium text-gray-700 mb-2 block" data-id="element-2869">
                      Registration Source
                    </label>
                    <div className="space-y-2" data-id="element-2870">
                      <label className="flex items-center gap-2 cursor-pointer" data-id="element-2871">
                        <input type="radio" checked={selectedRegistrationSource === 'all'} onChange={() => setSelectedRegistrationSource('all')} className="text-blue-600 focus:ring-blue-500" data-id="element-2872" />
                        <span className="text-sm text-gray-700" data-id="element-2873">
                          All Sources
                        </span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer" data-id="element-2874">
                        <input type="radio" checked={selectedRegistrationSource === 'self-registered'} onChange={() => setSelectedRegistrationSource('self-registered')} className="text-blue-600 focus:ring-blue-500" data-id="element-2875" />
                        <span className="text-sm text-gray-700" data-id="element-2876">
                          Self Registered
                        </span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer" data-id="element-2877">
                        <input type="radio" checked={selectedRegistrationSource === 'admin-invited'} onChange={() => setSelectedRegistrationSource('admin-invited')} className="text-blue-600 focus:ring-blue-500" data-id="element-2878" />
                        <span className="text-sm text-gray-700" data-id="element-2879">Invited</span>
                      </label>
                    </div>
                  </div>

                  {/* Status Filter */}
                  <div data-id="element-2880">
                    <label className="text-xs font-medium text-gray-700 mb-2 block" data-id="element-2881">
                      Status
                    </label>
                    <div className="space-y-2" data-id="element-2882">
                      <label className="flex items-center gap-2 cursor-pointer" data-id="element-2883">
                        <input type="radio" checked={selectedStatus === 'all'} onChange={() => setSelectedStatus('all')} className="text-blue-600 focus:ring-blue-500" data-id="element-2884" />
                        <span className="text-sm text-gray-700" data-id="element-2885">
                          All Status
                        </span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer" data-id="element-2886">
                        <input type="radio" checked={selectedStatus === 'active'} onChange={() => setSelectedStatus('active')} className="text-blue-600 focus:ring-blue-500" data-id="element-2887" />
                        <span className="text-sm text-gray-700" data-id="element-2888">Active</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer" data-id="element-2889">
                        <input type="radio" checked={selectedStatus === 'pending'} onChange={() => setSelectedStatus('pending')} className="text-blue-600 focus:ring-blue-500" data-id="element-2890" />
                        <span className="text-sm text-gray-700" data-id="element-2891">Pending</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer" data-id="element-2892">
                        <input type="radio" checked={selectedStatus === 'suspended'} onChange={() => setSelectedStatus('suspended')} className="text-blue-600 focus:ring-blue-500" data-id="element-2893" />
                        <span className="text-sm text-gray-700" data-id="element-2894">Suspended</span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>}
          </div>
        </div>

        {/* Active Filters Summary */}
        {activeFiltersCount > 0 && <div className="mt-3 pt-3 border-t border-gray-200 flex items-center gap-2 flex-wrap" data-id="element-2895">
            <span className="text-xs font-medium text-gray-500" data-id="element-2896">
              Active filters:
            </span>
            {filterNoExhibitor && <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded flex items-center gap-1" data-id="element-2897">
                No Exhibitor
                <button onClick={() => setFilterNoExhibitor(false)} className="hover:text-blue-900" data-id="element-2898">
                  <XIcon className="w-3 h-3" data-id="element-2899" />
                </button>
              </span>}
            {filterNotLoggedIn && <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded flex items-center gap-1" data-id="element-2900">
                Not Logged In
                <button onClick={() => setFilterNotLoggedIn(false)} className="hover:text-blue-900" data-id="element-2901">
                  <XIcon className="w-3 h-3" data-id="element-2902" />
                </button>
              </span>}
            {filterIncompleteProfile && <span className="px-2 py-1 bg-orange-100 text-orange-700 text-xs rounded flex items-center gap-1" data-id="element-2903">
                Incomplete Profiles
                <button onClick={() => setFilterIncompleteProfile(false)} className="hover:text-orange-900" data-id="element-2904">
                  <XIcon className="w-3 h-3" data-id="element-2905" />
                </button>
              </span>}
            {filterIncompleteOnboarding && <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded flex items-center gap-1" data-id="element-2906">
                Incomplete Onboarding
                <button onClick={() => setFilterIncompleteOnboarding(false)} className="hover:text-purple-900" data-id="element-2907">
                  <XIcon className="w-3 h-3" data-id="element-2908" />
                </button>
              </span>}
            {selectedPermissions.map(permission => <span key={permission} className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded flex items-center gap-1" data-id="element-2909">
                {getPermissionLabel(permission)}
                <button onClick={() => togglePermission(permission)} className="hover:text-gray-900" data-id="element-2910">
                  <XIcon className="w-3 h-3" data-id="element-2911" />
                </button>
              </span>)}
            {selectedRegistrationSource !== 'all' && <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded flex items-center gap-1" data-id="element-2912">
                Source: {getRegistrationSourceLabel(selectedRegistrationSource)}
                <button onClick={() => setSelectedRegistrationSource('all')} className="hover:text-gray-900" data-id="element-2913">
                  <XIcon className="w-3 h-3" data-id="element-2914" />
                </button>
              </span>}
            {selectedStatus !== 'all' && <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded flex items-center gap-1" data-id="element-2915">
                Status: {selectedStatus}
                <button onClick={() => setSelectedStatus('all')} className="hover:text-gray-900" data-id="element-2916">
                  <XIcon className="w-3 h-3" data-id="element-2917" />
                </button>
              </span>}
          </div>}
      </div>

      {/* Results Count */}
      <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 mb-4" data-id="element-2918">
        <p className="text-sm text-gray-600 font-gotham" data-id="element-2919">
          {filteredUsers.length} user{filteredUsers.length !== 1 ? 's' : ''}{' '}
          found
        </p>
      </div>

      {/* Mobile Card View (< sm) */}
      <div className="block sm:hidden px-4 space-y-4" data-id="element-2920">
        {filteredUsers.map(user => <Card key={user.id} padding="md" className="relative cursor-pointer" onClick={() => handleUserClick(user)} data-id="element-2921">
            <div className="flex items-start justify-between mb-3" data-id="element-2922">
              <div className="flex items-center gap-3" data-id="element-2923">
                <div className="h-10 w-10 bg-gray-100 rounded-full flex items-center justify-center" data-id="element-2924">
                  <UserIcon className="w-5 h-5 text-gray-500" data-id="element-2925" />
                </div>
                <div data-id="element-2926">
                  <h3 className="font-bold text-gray-900" data-id="element-2927">{user.name}</h3>
                  <p className="text-sm text-gray-500" data-id="element-2928">{user.email}</p>
                </div>
              </div>
              <div className="relative" data-id="element-2929">
                <button onClick={e => {
              e.stopPropagation();
              setActiveActionMenu(activeActionMenu === user.id ? null : user.id);
            }} className="p-2 hover:bg-gray-100 rounded-full" data-id="element-2930">
                  <MoreVerticalIcon className="w-5 h-5 text-gray-400" data-id="element-2931" />
                </button>
                {activeActionMenu === user.id && <>
                    <div className="fixed inset-0 z-10" onClick={() => setActiveActionMenu(null)} data-id="element-2932" />
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-20 py-1" data-id="element-2933">
                      <button className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2" data-id="element-2934">
                        <EditIcon className="w-4 h-4" data-id="element-2935" /> Edit User
                      </button>
                      {user.permissionProfile === 'b2b-access' && <button onClick={() => {
                  handleOpenLinkModal(user);
                  setActiveActionMenu(null);
                }} className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2" data-id="element-2936">
                          <LinkIcon className="w-4 h-4" data-id="element-2937" /> Link Exhibitor
                        </button>}
                      {user.status === 'suspended' ? <button onClick={() => handleUnsuspendUser(user.id)} className="w-full px-4 py-2 text-left text-sm text-green-600 hover:bg-green-50 flex items-center gap-2" data-id="element-2938">
                          <CheckCircleIcon className="w-4 h-4" data-id="element-2939" /> Unsuspend User
                        </button> : <button onClick={() => handleSuspendUser(user.id)} className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2" data-id="element-2940">
                          <BanIcon className="w-4 h-4" data-id="element-2941" /> Suspend User
                        </button>}
                    </div>
                  </>}
              </div>
            </div>

            <div className="space-y-2 mb-3" data-id="element-2942">
              <div className="flex items-center justify-between text-sm" data-id="element-2943">
                <span className="text-gray-500" data-id="element-2944">Permissions</span>
                <div className="flex gap-1 flex-wrap justify-end" data-id="element-2945">
                  {user.permissions.map(permission => <Badge key={permission} variant={getPermissionBadgeVariant(permission)} size="sm" data-id="element-2946">
                      {getPermissionLabel(permission)}
                    </Badge>)}
                </div>
              </div>
              <div className="flex items-center justify-between text-sm" data-id="element-2947">
                <span className="text-gray-500" data-id="element-2948">Status</span>
                <Badge variant={getStatusBadgeVariant(user.status)} data-id="element-2949">
                  {user.status}
                </Badge>
              </div>
              {(user.companyName || user.permissions.includes('b2b-access')) && <div className="flex items-center justify-between text-sm" data-id="element-2950">
                  <span className="text-gray-500" data-id="element-2951">Company</span>
                  {user.companyName ? <span className="font-medium text-right" data-id="element-2952">
                      {user.companyName}
                    </span> : <span className="text-gray-400 italic" data-id="element-2953">
                      No company linked
                    </span>}
                </div>}
            </div>
          </Card>)}
      </div>

      {/* Desktop Table View (>= sm) */}
      <div className="hidden sm:block px-4 sm:px-0" data-id="element-2954">
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm" data-id="element-2955">
          <div className="overflow-x-auto" data-id="element-2956">
            <table className="w-full min-w-[800px] text-left text-sm font-inter" data-id="element-2957">
              <thead className="bg-gray-50 border-b border-gray-200" data-id="element-2958">
                <tr data-id="element-2959">
                  <th className="px-6 py-3 font-semibold text-gray-900 sticky left-0 bg-gray-50 z-10 min-w-[280px]" data-id="element-2960">
                    User
                  </th>
                  <th className="px-6 py-3 font-semibold text-gray-900 min-w-[200px]" data-id="element-2961">
                    Permissions
                  </th>
                  <th className="px-6 py-3 font-semibold text-gray-900 min-w-[200px]" data-id="element-2962">
                    Company / Exhibitor
                  </th>
                  <th className="px-6 py-3 font-semibold text-gray-900 min-w-[120px]" data-id="element-2963">
                    Status
                  </th>
                  <th className="px-6 py-3 font-semibold text-gray-900 min-w-[140px]" data-id="element-2964">
                    Registered
                  </th>
                  <th className="px-6 py-3 font-semibold text-gray-900 min-w-[120px]" data-id="element-2965">
                    Last Login
                  </th>
                  <th className="px-6 py-3 font-semibold text-gray-900 text-right sticky right-0 bg-gray-50 z-10 min-w-[80px]" data-id="element-2966">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200" data-id="element-2967">
                {filteredUsers.map(user => <tr key={user.id} onClick={() => handleUserClick(user)} className="group hover:bg-gray-50 cursor-pointer transition-colors" data-id="element-2968">
                    <td className="px-6 py-4 sticky left-0 bg-white group-hover:bg-gray-50 z-10 transition-colors" data-id="element-2969">
                      <div className="flex items-center" data-id="element-2970">
                        <div className="flex-shrink-0 h-10 w-10 bg-gray-200 rounded-full flex items-center justify-center" data-id="element-2971">
                          <UserIcon className="w-5 h-5 text-gray-500" data-id="element-2972" />
                        </div>
                        <div className="ml-4" data-id="element-2973">
                          <div className="text-sm font-medium text-gray-900" data-id="element-2974">
                            {user.name}
                          </div>
                          <div className="text-sm text-gray-500" data-id="element-2975">
                            {user.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 bg-white group-hover:bg-gray-50 transition-colors" data-id="element-2976">
                      <div className="flex gap-1 flex-wrap" data-id="element-2977">
                        {user.permissions.map(permission => <Badge key={permission} variant={getPermissionBadgeVariant(permission)} size="sm" data-id="element-2978">
                            {getPermissionLabel(permission)}
                          </Badge>)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap bg-white group-hover:bg-gray-50 transition-colors" data-id="element-2979">
                      {user.companyName ? <div data-id="element-2980">
                          <div className="flex items-center gap-2" data-id="element-2981">
                            <div className="text-sm font-medium text-gray-900" data-id="element-2982">
                              {user.companyName}
                            </div>
                            {user.isPrimaryContact && <Badge variant="default" size="sm" data-id="element-2983">
                                Primary
                              </Badge>}
                          </div>
                          <div className="text-sm text-gray-500" data-id="element-2984">
                            {user.role}
                            {user.standNumber && ` • Stand ${user.standNumber}`}
                          </div>
                        </div> : user.permissions.includes('b2b-access') ? <LinkingStatusBadge status="no-exhibitor" onClick={() => handleOpenLinkModal(user)} data-id="element-2985" /> : <span className="text-sm text-gray-400" data-id="element-2986">—</span>}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap bg-white group-hover:bg-gray-50 transition-colors" data-id="element-2987">
                      <Badge variant={getStatusBadgeVariant(user.status)} data-id="element-2988">
                        {user.status}
                      </Badge>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap bg-white group-hover:bg-gray-50 transition-colors" data-id="element-2989">
                      <div className="text-sm text-gray-900" data-id="element-2990">
                        {new Date(user.createdAt).toLocaleDateString()}
                      </div>
                      <div className="text-xs text-gray-500" data-id="element-2991">
                        {user.registrationSource === 'self-registered' ? 'Self-registered' : 'Admin invited'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 bg-white group-hover:bg-gray-50 transition-colors" data-id="element-2992">
                      {user.lastLogin ? new Date(user.lastLogin).toLocaleDateString() : 'Never'}
                    </td>
                    <td className="px-6 py-4 text-right sticky right-0 bg-white group-hover:bg-gray-50 transition-colors" onClick={e => e.stopPropagation()} style={{
                  zIndex: activeActionMenu === user.id ? 0 : 10
                }} data-id="element-2993">
                      <div className="relative" data-id="element-2994">
                        <button data-user-id={user.id} onClick={() => setActiveActionMenu(activeActionMenu === user.id ? null : user.id)} className="text-gray-400 hover:text-gray-600 relative z-20" data-id="element-2995">
                          <MoreVerticalIcon className="w-5 h-5" data-id="element-2996" />
                        </button>
                      </div>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>

          {/* Actions Menu - Rendered outside table */}
          {activeActionMenu && <div className="fixed inset-0 z-50" onClick={() => setActiveActionMenu(null)} data-id="element-2997">
              {filteredUsers.map(user => {
            if (activeActionMenu !== user.id) return null;
            return <div key={user.id} ref={actionsMenuRef} className="absolute w-48 bg-white rounded-lg shadow-lg border border-gray-200" style={{
              top: `${document.querySelector(`[data-user-id="${user.id}"]`)?.getBoundingClientRect().bottom || 0}px`,
              right: '2rem'
            }} onClick={e => e.stopPropagation()} data-id="element-2998">
                    <button className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2 border-b border-gray-100" data-id="element-2999">
                      <EditIcon className="w-4 h-4" data-id="element-3000" /> Edit User
                    </button>
                    {user.permissionProfile === 'b2b-access' && <button onClick={() => {
                handleOpenLinkModal(user);
                setActiveActionMenu(null);
              }} className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2 border-b border-gray-100" data-id="element-3001">
                        <LinkIcon className="w-4 h-4" data-id="element-3002" /> Link Exhibitor
                      </button>}
                    {user.status === 'suspended' ? <button onClick={() => handleUnsuspendUser(user.id)} className="w-full px-4 py-2 text-left text-sm text-green-600 hover:bg-green-50 flex items-center gap-2" data-id="element-3003">
                        <CheckCircleIcon className="w-4 h-4" data-id="element-3004" /> Unsuspend User
                      </button> : <button onClick={() => handleSuspendUser(user.id)} className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2" data-id="element-3005">
                        <BanIcon className="w-4 h-4" data-id="element-3006" /> Suspend User
                      </button>}
                  </div>;
          })}
            </div>}
        </div>
      </div>

      {/* Invite User Modal */}
      {showInviteModal && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-3007">
          <Card className="w-full max-w-2xl" data-id="element-3008">
            <div className="p-6 max-h-[85vh] overflow-y-auto" data-id="element-3009">
              <h3 className="text-lg font-bold text-gray-900 mb-6" data-id="element-3010">
                Invite User
              </h3>
              <div className="space-y-6" data-id="element-3011">
                <div className="grid grid-cols-2 gap-4" data-id="element-3012">
                  <div data-id="element-3013">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3014">
                      Email Address *
                    </label>
                    <Input type="email" placeholder="user@company.com" value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} data-id="element-3015" />
                  </div>
                  <div data-id="element-3016">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3017">
                      Full Name *
                    </label>
                    <Input type="text" placeholder="John Doe" value={inviteName} onChange={e => setInviteName(e.target.value)} data-id="element-3018" />
                  </div>
                  <div data-id="element-3019">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3020">
                      Role
                    </label>
                    <Input type="text" placeholder="e.g., Sales Manager" value={inviteRole} onChange={e => setInviteRole(e.target.value)} data-id="element-3021" />
                  </div>

                  {/* Permissions Selection */}
                  <div className="border-t border-gray-200 pt-4" data-id="element-3022">
                    <label className="block text-sm font-medium text-gray-700 mb-3" data-id="element-3023">
                      Permissions *
                    </label>
                    <div className="space-y-2" data-id="element-3024">
                      <label className="flex items-start gap-3 cursor-pointer p-3 border border-gray-200 rounded-lg hover:bg-gray-50" data-id="element-3025">
                        <input type="checkbox" checked={invitePermissions.includes('general-public')} onChange={() => toggleInvitePermission('general-public')} className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500" data-id="element-3026" />
                        <div data-id="element-3027">
                          <div className="text-sm font-medium text-gray-900" data-id="element-3028">
                            General Public
                          </div>
                          <div className="text-xs text-gray-500 mt-0.5" data-id="element-3029">
                            Access to public features and event information
                          </div>
                        </div>
                      </label>

                      <label className="flex items-start gap-3 cursor-pointer p-3 border border-gray-200 rounded-lg hover:bg-gray-50" data-id="element-3030">
                        <input type="checkbox" checked={invitePermissions.includes('b2b-access')} onChange={() => toggleInvitePermission('b2b-access')} className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500" data-id="element-3031" />
                        <div data-id="element-3032">
                          <div className="text-sm font-medium text-gray-900" data-id="element-3033">
                            B2B Access
                          </div>
                          <div className="text-xs text-gray-500 mt-0.5" data-id="element-3034">
                            Networking tools, meeting scheduler, and business
                            resources
                          </div>
                        </div>
                      </label>

                      <label className="flex items-start gap-3 cursor-pointer p-3 border border-gray-200 rounded-lg hover:bg-gray-50" data-id="element-3035">
                        <input type="checkbox" checked={invitePermissions.includes('buyer')} onChange={() => toggleInvitePermission('buyer')} className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500" data-id="element-3036" />
                        <div data-id="element-3037">
                          <div className="text-sm font-medium text-gray-900" data-id="element-3038">
                            Buyer
                          </div>
                          <div className="text-xs text-gray-500 mt-0.5" data-id="element-3039">
                            Buyer tools, procurement resources, and supplier
                            discovery (includes B2B Access)
                          </div>
                        </div>
                      </label>

                      <label className="flex items-start gap-3 cursor-pointer p-3 border border-gray-200 rounded-lg hover:bg-gray-50" data-id="element-3040">
                        <input type="checkbox" checked={invitePermissions.includes('speaker')} onChange={() => toggleInvitePermission('speaker')} className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500" data-id="element-3041" />
                        <div data-id="element-3042">
                          <div className="text-sm font-medium text-gray-900" data-id="element-3043">
                            Speaker
                          </div>
                          <div className="text-xs text-gray-500 mt-0.5" data-id="element-3044">
                            Session management, presentation tools, and speaker
                            resources (includes B2B Access)
                          </div>
                        </div>
                      </label>
                    </div>
                  </div>
                </div>

                {/* Exhibitor Linking (only for B2B Access users) */}
                {invitePermissions.includes('b2b-access') && <div className="border-t border-gray-200 pt-6" data-id="element-3045">
                    <div className="flex items-center justify-between mb-4" data-id="element-3046">
                      <div data-id="element-3047">
                        <h4 className="text-sm font-semibold text-gray-900" data-id="element-3048">
                          Link to Exhibitors
                        </h4>
                        <p className="text-xs text-gray-500 mt-1" data-id="element-3049">
                          Connect this user to one or more exhibitor companies
                        </p>
                      </div>
                      <label className="flex items-center gap-2 cursor-pointer" data-id="element-3050">
                        <input type="checkbox" checked={skipExhibitorLink} onChange={e => {
                    setSkipExhibitorLink(e.target.checked);
                    if (e.target.checked) {
                      setSelectedExhibitorIds([]);
                      setShowCreateExhibitor(false);
                    }
                  }} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" data-id="element-3051" />
                        <span className="text-sm text-gray-600" data-id="element-3052">
                          Skip for now
                        </span>
                      </label>
                    </div>

                    {!skipExhibitorLink && <div className="space-y-4" data-id="element-3053">
                        {!showCreateExhibitor ? <>
                            <div className="space-y-2" data-id="element-3054">
                              {selectedExhibitorIds.map(id => {
                      const exhibitor = mockExhibitors.find(e => e.id === id);
                      if (!exhibitor) return null;
                      return <div key={id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200" data-id="element-3055">
                                    <div className="flex items-center gap-3" data-id="element-3056">
                                      <BuildingIcon className="w-4 h-4 text-gray-500" data-id="element-3057" />
                                      <div data-id="element-3058">
                                        <div className="text-sm font-medium text-gray-900" data-id="element-3059">
                                          {exhibitor.name}
                                        </div>
                                        <div className="text-xs text-gray-500" data-id="element-3060">
                                          Stand {exhibitor.standNumber}
                                        </div>
                                      </div>
                                    </div>
                                    <button onClick={() => handleRemoveExhibitor(id)} className="text-gray-400 hover:text-red-500" data-id="element-3061">
                                      <XIcon className="w-4 h-4" data-id="element-3062" />
                                    </button>
                                  </div>;
                    })}
                            </div>

                            <ExhibitorSelector exhibitors={mockExhibitors} selectedExhibitorId={undefined} onSelect={handleAddExhibitor} onCreateNew={() => setShowCreateExhibitor(true)} placeholder="Add another exhibitor..." data-id="element-3063" />

                            <button type="button" onClick={() => setShowCreateExhibitor(true)} className="text-sm text-blue-600 hover:text-blue-700 font-medium" data-id="element-3064">
                              + Create new exhibitor instead
                            </button>
                          </> : <div className="space-y-4 p-4 bg-blue-50 rounded-lg border border-blue-200" data-id="element-3065">
                            <div className="flex items-center justify-between" data-id="element-3066">
                              <h5 className="text-sm font-semibold text-gray-900" data-id="element-3067">
                                Quick Exhibitor Creation
                              </h5>
                              <button type="button" onClick={() => {
                      setShowCreateExhibitor(false);
                      setNewExhibitorName('');
                      setNewExhibitorStand('');
                    }} className="text-gray-400 hover:text-gray-600" data-id="element-3068">
                                <XIcon className="w-4 h-4" data-id="element-3069" />
                              </button>
                            </div>
                            <div className="grid grid-cols-2 gap-3" data-id="element-3070">
                              <div data-id="element-3071">
                                <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-3072">
                                  Company Name *
                                </label>
                                <Input type="text" placeholder="Acme Corporation" value={newExhibitorName} onChange={e => setNewExhibitorName(e.target.value)} className="text-sm" data-id="element-3073" />
                              </div>
                              <div data-id="element-3074">
                                <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-3075">
                                  Stand Number *
                                </label>
                                <Input type="text" placeholder="A123" value={newExhibitorStand} onChange={e => setNewExhibitorStand(e.target.value)} className="text-sm" data-id="element-3076" />
                              </div>
                            </div>
                            <div data-id="element-3077">
                              <label className="block text-xs font-medium text-gray-700 mb-2" data-id="element-3078">
                                Events *
                              </label>
                              <div className="flex gap-2" data-id="element-3079">
                                {availableEvents.map(event => <label key={event.id} className="flex items-center gap-2 cursor-pointer" data-id="element-3080">
                                    <input type="checkbox" checked={newExhibitorEvents.includes(event.id)} onChange={e => {
                          if (e.target.checked) {
                            setNewExhibitorEvents([...newExhibitorEvents, event.id]);
                          } else {
                            setNewExhibitorEvents(newExhibitorEvents.filter(id => id !== event.id));
                          }
                        }} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" data-id="element-3081" />
                                    <span className="text-xs text-gray-700" data-id="element-3082">
                                      {event.id}
                                    </span>
                                  </label>)}
                              </div>
                            </div>
                          </div>}
                      </div>}

                    {skipExhibitorLink && <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg" data-id="element-3083">
                        <p className="text-xs text-yellow-800" data-id="element-3084">
                          ⚠️ This user will be created without an exhibitor
                          link. You can link them later from the users table.
                        </p>
                      </div>}
                  </div>}
              </div>

              <div className="flex gap-3 mt-8" data-id="element-3085">
                <Button variant="outline" onClick={() => setShowInviteModal(false)} className="flex-1" data-id="element-3086">
                  Cancel
                </Button>
                <Button variant="primary" className="flex-1" onClick={handleInviteUser} disabled={!inviteEmail || !inviteName || showCreateExhibitor && (!newExhibitorName || !newExhibitorStand)} data-id="element-3087">
                  <MailIcon className="w-4 h-4 mr-2" data-id="element-3088" />
                  Send Invitation
                </Button>
              </div>
            </div>
          </Card>
        </div>}

      {/* Link User to Exhibitor Modal */}
      {showLinkModal && userToLink && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-3089">
          <Card className="w-full max-w-md" data-id="element-3090">
            <div className="p-6" data-id="element-3091">
              <h3 className="text-lg font-bold text-gray-900 mb-4" data-id="element-3092">
                Link User to Exhibitor
              </h3>
              <div className="space-y-4" data-id="element-3093">
                <div className="p-3 bg-gray-50 rounded-lg" data-id="element-3094">
                  <div className="text-sm font-medium text-gray-900" data-id="element-3095">
                    {userToLink.name}
                  </div>
                  <div className="text-xs text-gray-500" data-id="element-3096">
                    {userToLink.email}
                  </div>
                </div>

                <div data-id="element-3097">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-3098">
                    Select Exhibitors
                  </label>

                  <div className="space-y-2 mb-3" data-id="element-3099">
                    {selectedExhibitorIds.map(id => {
                  const exhibitor = mockExhibitors.find(e => e.id === id);
                  if (!exhibitor) return null;
                  return <div key={id} className="flex items-center justify-between p-2 bg-gray-50 rounded border border-gray-200" data-id="element-3100">
                          <span className="text-sm" data-id="element-3101">{exhibitor.name}</span>
                          <button onClick={() => handleRemoveExhibitor(id)} className="text-gray-400 hover:text-red-500" data-id="element-3102">
                            <XIcon className="w-4 h-4" data-id="element-3103" />
                          </button>
                        </div>;
                })}
                  </div>

                  <ExhibitorSelector exhibitors={mockExhibitors} selectedExhibitorId={undefined} onSelect={handleAddExhibitor} placeholder="Search exhibitors..." data-id="element-3104" />
                </div>
              </div>

              <div className="flex gap-3 mt-6" data-id="element-3105">
                <Button variant="outline" onClick={() => {
              setShowLinkModal(false);
              setUserToLink(null);
            }} className="flex-1" data-id="element-3106">
                  Cancel
                </Button>
                <Button variant="primary" className="flex-1" onClick={handleLinkUser} disabled={selectedExhibitorIds.length === 0} data-id="element-3107">
                  Link User
                </Button>
              </div>
            </div>
          </Card>
        </div>}
    </div>;
}